import React, { useMemo } from 'react'
import { useDraggableSheet } from '../../../helpers/hooks';
import './PriceDetails.scss';
import Icon from '../Icon/Icon';
import { HYK_FEE_PERCENT, STRIPE_FEE_PERCENT, STRIPE_FIXED_FEE, VAT_PERCENT } from '../../constants/globals';

const PriceDetails = ({ onClose, creation, onContribute, quantity = 1, isPhysical = false, shippingMethod = null, sizeSelections = [], requiresSizeSelection = false }) => {
    const {
        // dragOffset,
        // dragging,
        sheetStyle,
        overlayHandlers
    } = useDraggableSheet({ onClose });
    
    // Calculate fees based on creation.price
    const basePrice = parseFloat(creation?.price) || 0;
    const isNotFree = creation ? parseFloat((creation || {})?.price || 0) > 0 : false;

    // Calculate items total (by size if size selections exist, otherwise base price * quantity)
    const itemsTotal = useMemo(() => {
        if (requiresSizeSelection && sizeSelections.length > 0) {
            // Sum up all size selections: each size has quantity * basePrice
            return sizeSelections.reduce((sum, selection) => {
                const qty = selection.quantity || 0;
                return sum + (basePrice * qty);
            }, 0);
        }
        // For non-size items, just base price * quantity
        return basePrice * quantity;
    }, [requiresSizeSelection, sizeSelections, basePrice, quantity]);

    // Calculate shipping cost for physical creations
    const shippingCost = useMemo(() => {
        if (!isPhysical || !shippingMethod || !shippingMethod.rates || shippingMethod.rates.length === 0) {
            return 0;
        }

        // Sort rates by sort_order to get the first/default rate
        const sortedRates = [...shippingMethod.rates].sort((a, b) => {
            const orderA = a.sort_order ?? 0;
            const orderB = b.sort_order ?? 0;
            return orderA - orderB;
        });

        const firstRate = sortedRates[0];
        if (!firstRate) return 0;

        const baseRate = parseFloat(firstRate.rate) || 0;
        const additionalItemRate = firstRate.additional_item_rate ? parseFloat(firstRate.additional_item_rate) : 0;
        
        // Calculate: base rate + (quantity - 1) * additional_item_rate
        const additionalItems = Math.max(0, quantity - 1);
        const totalShipping = baseRate + (additionalItems * additionalItemRate);
        
        return totalShipping;
    }, [isPhysical, shippingMethod, quantity]);

    // Creator gets = items total + shipping
    const creatorGets = useMemo(() => {
        return itemsTotal + shippingCost;
    }, [itemsTotal, shippingCost]);

    // Calculate fees on creator gets (items + shipping)
    const fees = useMemo(() => {
        if (creatorGets === 0) {
            return {
                hykFee: 0,
                stripeFee: 0,
                vatAmount: 0,
                totalAmount: 0
            };
        }

        const hykFee = creatorGets * HYK_FEE_PERCENT;
        const stripeFee = (creatorGets * STRIPE_FEE_PERCENT) + STRIPE_FIXED_FEE;
        const subtotal = creatorGets + hykFee + stripeFee;
        const vatAmount = subtotal * VAT_PERCENT;
        const totalAmount = subtotal + vatAmount;

        return {
            hykFee: Math.round(hykFee * 100) / 100,
            stripeFee: Math.round(stripeFee * 100) / 100,
            vatAmount: Math.round(vatAmount * 100) / 100,
            totalAmount: Math.round(totalAmount * 100) / 100
        };
    }, [creatorGets]);

    return (
        <div
            className="about-creator "
            {...overlayHandlers}
        >
            <div
                className={`sheet p-24`}
                style={{ ...sheetStyle }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="price-details-modal">
                    {isNotFree ? (
                        // Paid Creation Content
                        <>
                            <p className="modal-title ta-center fs-real fw-medium-bold fg-dark mb-24 mt-0">Payment details</p>

                            <div className="breakdown-section mb-24">
                                <p className="breakdown-title fw-bold fg-dark mb-16">Breakdown</p>
                                <div className="price-breakdown">
                                    {/* Show individual size items if size selections exist */}
                                    {requiresSizeSelection && sizeSelections.length > 0 ? (
                                        <>
                                            {sizeSelections.map((selection, index) => {
                                                const itemPrice = basePrice * (selection.quantity || 0);
                                                return (
                                                    <div key={index} className="price-row d-flex aic mb-12">
                                                        <span className="price-label fw-medium">{selection.quantity}x Size {selection.size_label}</span>
                                                        <span className="price-amount ml-auto">€ {itemPrice.toFixed(2)}</span>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        /* For non-size items, show quantity if > 1 */
                                        quantity >= 1 ? (
                                            <div className="price-row d-flex aic mb-12">
                                                <span className="price-label fw-medium">{quantity}x Item</span>
                                                <span className="price-amount ml-auto">€ {itemsTotal.toFixed(2)}</span>
                                            </div>
                                        ) : null
                                    )}
                                    
                                    {/* Delivery fees for physical creations */}
                                    {isPhysical && shippingCost > 0 && (
                                        <div className="price-row d-flex aic mb-12">
                                            <span className="price-label fw-medium">+ Delivery fees</span>
                                            <span className="price-amount ml-auto">€ {shippingCost.toFixed(2)}</span>
                                        </div>
                                    )}
                                    
                                    {/* Creator gets (items + shipping) - bold and pink */}
                                    <div className="price-row d-flex aic mb-12">
                                        <span className="price-label fw-bold" style={{ color: '#FF8CDC' }}>Creator gets</span>
                                        <span className="price-amount ml-auto fw-bold" style={{ color: '#FF8CDC' }}>€ {creatorGets.toFixed(2)}</span>
                                    </div>
                                    
                                    {/* HYK fees */}
                                    <div className="price-row d-flex aic mb-12">
                                        <span className="price-label fw-medium">HYK fees (10%)</span>
                                        <span className="price-amount ml-auto">€ {fees.hykFee.toFixed(2)}</span>
                                    </div>
                                    
                                    {/* Stripe fees */}
                                    <div className="price-row d-flex aic mb-12">
                                        <span className="price-label fw-medium">Stripe fees (1,5%+ € 0,25)</span>
                                        <span className="price-amount ml-auto">€ {fees.stripeFee.toFixed(2)}</span>
                                    </div>
                                    
                                    {/* Total to pay - bold and pink */}
                                    <div className="price-row total-row d-flex aic">
                                        <span className="price-label fw-bold" style={{ color: '#FF8CDC' }}>Total to pay</span>
                                        <span className="price-amount ml-auto fw-bold" style={{ color: '#FF8CDC' }}>€ {fees.totalAmount.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="fees-explanation">
                                <p className="explanation-title fw-bold mb-16">Why am I paying these fees?</p>
                                <div className="explanation-list">
                                    <div className="explanation-item d-flex aic mb-12">
                                        <Icon alias="check_outline" stroke="#FF8CDC" color="none" width={24} />
                                        <span className='ml-8 fw-medium'>Keep HYK fair, independent, and free of ads</span>
                                    </div>
                                    <div className="explanation-item d-flex aic mb-12">
                                        <Icon alias="check_outline" stroke="#FF8CDC" color="none" width={24} />
                                        <span className='ml-8 fw-medium'>Support the creators you care about.</span>
                                    </div>
                                    <div className="explanation-item d-flex aic mb-12">
                                        <Icon alias="check_outline" stroke="#FF8CDC" color="none" width={24} />
                                        <span className='ml-8 fw-medium'>Pay for our infrastructure costs</span>
                                    </div>
                                    <div className="explanation-item d-flex aic mb-12">
                                        <Icon alias="check_outline" stroke="#FF8CDC" color="none" width={24} />
                                        <span className='ml-8 fw-medium'>Insure safe payments</span>
                                    </div>
                                </div>
                            </div>

                            {/* CANCEL button */}
                            <div className="d-flex jcc mt-24">
                                <span 
                                    onClick={onClose} 
                                    className='fs-xxs fw-bold fg-dark td-underline c-pointer'
                                >
                                    CANCEL
                                </span>
                            </div>
                        </>
                    ) : (
                        // Free Creation Content
                        <div className='d-flex f-column aic'>
                            <div><Icon alias={"contribute_big"} height={80} width={80} color='#FF3232' /></div>
                            <p className="fs-sm2 fw-bold fg-dark ta-center mt-24">This is free, </p>
                            <p className="fs-sm2 fw-bold fg-dark ta-center ">contribute to keep the magic alive.</p>
                            <button className='btn red-btn btn-xl btn-full rounded-xxl mt-48 fs-real fw-extra-bold' onClick={() => { onClose(); onContribute() }}>{"Contribute to " + (creation.users?.userName || creation.users?.firstName || 'this creator')}</button>
                            <span onClick={() => onClose()} className='fs-xxs fw-bold fg-dark td-underline d-flex jcc aic mt-24 mb-24' >CANCEL</span>

                        </div>
                    )}

                </div>
            </div>
        </div>
    )
}

export default PriceDetails